import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Sheet from "@mui/joy/Sheet";
import Card from "@mui/joy/Card";
import CardContent from "@mui/joy/CardContent";
import Button from "@mui/joy/Button";
import Chip from "@mui/joy/Chip";
import Divider from "@mui/joy/Divider";
import CircularProgress from "@mui/joy/CircularProgress";
import Avatar from "@mui/joy/Avatar";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import ModalClose from "@mui/joy/ModalClose";

import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import VolunteerActivismRoundedIcon from "@mui/icons-material/VolunteerActivismRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";

import PageBreadcrumbs from "../components/common/PageBreadcrumbs";
import DonationForm from "../components/Donation/DonationForm";
import { fetchEvent } from "../api";
import type { Event as EventDetail } from "../models/all";
import { formatISTDate } from "../utils/date";

type EventState = {
  loading: boolean;
  event: EventDetail | null;
  error: boolean;
};

const formatDate = (value?: string | null) => {
  if (!value) return null;
  try {
    return formatISTDate(value, "dddd, D MMM YYYY");
  } catch {
    return null;
  }
};

export default function EventPage() {
  const { id } = useParams();
  const [{ loading, event, error }, setState] = React.useState<EventState>({
    loading: true,
    event: null,
    error: false,
  });
  const [shareBusy, setShareBusy] = useState(false);
  const [posterOpen, setPosterOpen] = useState(false);

  const loadEvent = React.useCallback(async () => {
    if (!id) return;
    setState((prev) => ({ ...prev, loading: true, error: false }));
    try {
      const data = await fetchEvent(id);
      setState({ loading: false, event: (data as EventDetail) ?? null, error: false });
    } catch (err) {
      console.error("Failed to load event", err);
      setState({ loading: false, event: null, error: true });
    }
  }, [id]);

  React.useEffect(() => {
    void loadEvent();
  }, [loadEvent]);

  const eventDate = useMemo(() => formatDate(event?.startDate), [event?.startDate]);

  const metadataCards = useMemo(() => {
    if (!event) return [] as Array<{ label: string; helper?: string; value: string; icon: React.ReactNode }>;
    const cards: Array<{ label: string; helper?: string; value: string; icon: React.ReactNode }> = [];
    if (eventDate) {
      cards.push({ label: "Date", value: eventDate, icon: <CalendarMonthRoundedIcon fontSize="small" /> });
    }
    if (event.location) {
      cards.push({ label: "Venue", value: event.location, icon: <PlaceRoundedIcon fontSize="small" /> });
    }
    return cards;
  }, [event, eventDate]);

  const handleScrollToForm = () => {
    const target = document.getElementById("event-donation-form");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleShare = async () => {
    if (!event) return;
    try {
      setShareBusy(true);
      const url = window.location.href;
      if (navigator.share) {
        await navigator.share({
          title: event.title,
          text: event.purpose || "Join us for this celebration",
          url,
        });
      } else {
        const summary = `${event.title}\n${eventDate ?? ""}\n${event.location ?? ""}\n${url}`.trim();
        await navigator.clipboard.writeText(summary);
        alert("Link copied to clipboard!");
      }
    } catch (error) {
      console.error("Share failed", error);
    } finally {
      setShareBusy(false);
    }
  };

  const handlePosterOpen = () => {
    if (event?.poster) {
      setPosterOpen(true);
    }
  };

  const handlePosterClose = () => {
    setPosterOpen(false);
  };

  if (loading) {
    return (
      <Box
        component="main"
        className="MainContent"
        sx={{
          px: { xs: 2, md: 6 },
          pt: { xs: "calc(12px + var(--Header-height))", md: 3 },
          pb: { xs: 2, md: 3 },
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.body",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!event) {
    return (
      <Box
        component="main"
        className="MainContent"
        sx={{
          px: { xs: 2, md: 6 },
          pt: { xs: "calc(12px + var(--Header-height))", md: 3 },
          pb: { xs: 2, md: 3 },
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.body",
        }}
      >
        <Typography level="title-lg">Event not found</Typography>
      </Box>
    );
  }

  const posterDescription = event.title ? `${event.title} poster` : "Event poster";

  return (
    <>
      <Stack spacing={3} sx={{ width: "100%", maxWidth: 1100 }}>
        <PageBreadcrumbs links={[{ label: "Events", href: "/events" }]} current={event.title || "Event"} />

        <Sheet
          variant="outlined"
          sx={{
            borderRadius: "xl",
            p: { xs: 2, sm: 2.5, md: 3 },
            boxShadow: { xs: "var(--joy-shadow-sm)", md: "var(--joy-shadow-md)" },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2.5, md: 3 },
            alignItems: { xs: "stretch", md: "center" },
          }}
        >
          {event.poster ? (
            <Box
              sx={{
                order: { xs: 1, md: 2 },
                width: "100%",
                maxWidth: { xs: 320, sm: 340, md: 320 },
                mx: { xs: "auto", md: 0 },
                flexShrink: 0,
              }}
            >
              <Box
                component="button"
                type="button"
                onClick={handlePosterOpen}
                aria-label={`View ${posterDescription} in full size`}
                title={`View ${posterDescription}`}
                sx={{
                  width: "100%",
                  border: "none",
                  p: 0,
                  backgroundColor: "transparent",
                  borderRadius: "xl",
                  display: "block",
                  cursor: "zoom-in",
                  boxShadow: { xs: "var(--joy-shadow-sm)", md: "0 36px 64px rgba(8,12,24,0.28)" },
                  transition: "box-shadow 120ms ease",
                  overflow: "hidden",
                  "&:focus-visible": {
                    outline: "3px solid var(--joy-palette-primary-500)",
                    outlineOffset: 4,
                  },
                }}
              >
                <Box
                  component="img"
                  src={event.poster}
                  alt={event.title}
                  loading="lazy"
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    borderRadius: "inherit",
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Sheet
              variant="soft"
              sx={{
                order: { xs: 1, md: 2 },
                borderRadius: "xl",
                p: 3,
                textAlign: "center",
                maxWidth: { xs: 320, sm: 340, md: 320 },
                mx: { xs: "auto", md: 0 },
                flexShrink: 0,
              }}
            >
              <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                No poster available
              </Typography>
            </Sheet>
          )}
          <Stack spacing={{ xs: 1.5, md: 1.75 }} sx={{ flex: 1, order: { xs: 2, md: 1 } }}>
            <Stack spacing={0.5}>
              <Typography
                level="body-sm"
                sx={{ textTransform: "uppercase", letterSpacing: 0.8, color: "text.tertiary", fontWeight: 600 }}
              >
                Upcoming
              </Typography>
              <Typography
                level="h1"
                sx={{
                  fontSize: { xs: "clamp(1.85rem, 5.5vw, 2.4rem)", md: "clamp(2.3rem, 3.5vw, 2.7rem)" },
                  fontWeight: 700,
                  lineHeight: 1.1,
                }}
              >
                {event.title || "Untitled Event"}
              </Typography>
            </Stack>
            {event.purpose ? (
              <Typography level="body-md" sx={{ color: "text.tertiary", maxWidth: { xs: "100%", md: 720 }, lineHeight: 1.65 }}>
                {event.purpose}
              </Typography>
            ) : null}
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ rowGap: 0.75 }}>
              {eventDate ? (
                <Chip
                  size="sm"
                  variant="outlined"
                  startDecorator={<CalendarMonthRoundedIcon fontSize="small" />}
                  sx={{ borderRadius: "lg" }}
                >
                  {eventDate}
                </Chip>
              ) : null}
              {event.location ? (
                <Chip
                  size="sm"
                  variant="outlined"
                  startDecorator={<PlaceRoundedIcon fontSize="small" />}
                  sx={{ borderRadius: "lg" }}
                >
                  {event.location}
                </Chip>
              ) : null}
            </Stack>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 0.75, sm: 1 }}
              alignItems={{ xs: "stretch", sm: "center" }}
              sx={{ width: "100%" }}
            >
              <Button
                variant="solid"
                color="primary"
                endDecorator={<ArrowDownwardRoundedIcon fontSize="small" />}
                onClick={handleScrollToForm}
                sx={{
                  borderRadius: "lg",
                  fontWeight: 600,
                  px: { xs: 2, sm: 2.3 },
                  minHeight: 44,
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Contribute
              </Button>
              <Button
                variant="plain"
                color="neutral"
                onClick={handleShare}
                disabled={shareBusy}
                startDecorator={<ShareRoundedIcon fontSize="small" />}
                sx={{
                  borderRadius: "lg",
                  px: { xs: 2, sm: 1.6 },
                  minHeight: 44,
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: "center",
                }}
              >
                Share
              </Button>
              <Button
                component={Link}
                to={`/events/${event.id}/edit`}
                variant="plain"
                color="neutral"
                startDecorator={<EditRoundedIcon fontSize="small" />}
                sx={{
                  borderRadius: "lg",
                  px: { xs: 2, sm: 1.6 },
                  minHeight: 44,
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: "center",
                }}
              >
                Edit Event
              </Button>
            </Stack>
          </Stack>
        </Sheet>

        {metadataCards.length ? (
          <Box
            sx={{
              display: "grid",
              gap: { xs: 1, sm: 1.25 },
              gridTemplateColumns: {
                xs: "repeat(auto-fit, minmax(220px, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
            }}
          >
            {metadataCards.map((card) => (
              <Sheet
                key={card.label}
                variant="outlined"
                sx={{
                  borderRadius: "xl",
                  p: { xs: 1.5, md: 1.75 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  boxShadow: "var(--joy-shadow-xs)",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <Avatar size="sm" variant="soft" color="neutral" sx={{ borderRadius: "lg" }}>
                    {card.icon}
                  </Avatar>
                  <Typography level="body-xs" sx={{ textTransform: "uppercase", letterSpacing: 0.6, color: "text.tertiary" }}>
                    {card.label}
                  </Typography>
                </Stack>
                <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                  {card.value}
                </Typography>
                {card.helper ? (
                  <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                    {card.helper}
                  </Typography>
                ) : null}
              </Sheet>
            ))}
          </Box>
        ) : null}

        <Stack spacing={3} sx={{ width: "100%" }}>
          <Card variant="outlined" sx={{ borderRadius: "xl", borderColor: "divider", boxShadow: "var(--joy-shadow-md)" }}>
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Stack spacing={0.5}>
                <Typography level="title-md" sx={{ fontWeight: 700 }}>
                  About this celebration
                </Typography>
                <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                  Learn more about this event
                </Typography>
              </Stack>
              <Divider />
              {event.description ? (
                <Box
                  sx={{
                    "& h1, & h2, & h3": { fontSize: "1.15rem", fontWeight: 700, mt: 1.5 },
                    "& p": { lineHeight: 1.7, color: "text.primary" },
                    "& ul": { pl: 3 },
                  }}
                >
                  <ReactMarkdown>{event.description}</ReactMarkdown>
                </Box>
              ) : (
                <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                  No description available
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card id="event-donation-form" variant="outlined" sx={{ borderRadius: "xl", borderColor: "divider", boxShadow: "var(--joy-shadow-lg)" }}>
            <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
              >
                <Stack spacing={0.5}>
                  <Typography level="title-md" sx={{ fontWeight: 700 }}>
                    Support this event
                  </Typography>
                  <Typography level="body-sm" sx={{ color: "text.tertiary" }}>
                    Your contribution helps make this celebration possible
                  </Typography>
                </Stack>
                <Chip size="sm" variant="soft" color="primary" startDecorator={<VolunteerActivismRoundedIcon fontSize="small" />}>
                  Tax Receipt
                </Chip>
              </Stack>
              <DonationForm eventId={event.id} />
            </CardContent>
          </Card>
        </Stack>
      </Stack>
      {event.poster ? (
        <Modal
          open={posterOpen}
          onClose={handlePosterClose}
          keepMounted
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 2, sm: 3 },
          }}
        >
          <ModalDialog
            aria-label={posterDescription}
            variant="outlined"
            sx={{
              p: 0,
              borderRadius: "xl",
              overflow: "hidden",
              boxShadow: "var(--joy-shadow-lg)",
              maxWidth: "min(90vw, 720px)",
              width: "100%",
            }}
          >
            <ModalClose variant="plain" sx={{ m: 1 }} />
            <Box
              component="img"
              src={event.poster}
              alt={posterDescription}
              loading="lazy"
              sx={{
                display: "block",
                width: "100%",
                height: "100%",
                maxHeight: { xs: "80vh", sm: "85vh" },
                objectFit: "contain",
                bgcolor: "transparent",
              }}
            />
          </ModalDialog>
        </Modal>
      ) : null}
    </>
  );
}
